import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * The one date range the whole console works in.
 *
 * The presets are deliberately the exact four windows
 * /admin/payment-analytics accepts, because that endpoint takes a named
 * `period` and nothing else, while /admin/payment-stats and
 * /admin/payment-receipts take `startDate`/`endDate`. Keeping the picker's
 * vocabulary identical to the API's is what lets one control drive both kinds
 * of request honestly instead of one of them quietly ignoring it.
 *
 * A custom span still produces real dates for the two endpoints that accept
 * them; for the analytics endpoint it is widened to the smallest supported
 * window that covers it, and the consuming screen says so.
 */
const PRESETS = [
    { value: '7days', label: 'Last 7 days', days: 7 },
    { value: '30days', label: 'Last 30 days', days: 30 },
    { value: '90days', label: 'Last 90 days', days: 90 },
    { value: '1year', label: 'Last 12 months', days: 365 }
];

export const RANGE_PRESETS = PRESETS;

/** Local YYYY-MM-DD. `toISOString()` is UTC, which in IST lands on yesterday
 *  for anything before 05:30 — the backend makes the same point about dateKey. */
export const toDateInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const startOfWindow = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1));
    return date;
};

const rangeForPreset = (preset) => {
    const found = PRESETS.find((option) => option.value === preset) || PRESETS[1];
    return { startDate: toDateInput(startOfWindow(found.days)), endDate: toDateInput(new Date()) };
};

const DateRangeContext = createContext(null);

export const DateRangeProvider = ({ children }) => {
    const [preset, setPresetValue] = useState('30days');
    const [range, setRange] = useState(() => rangeForPreset('30days'));

    const value = useMemo(() => {
        const isCustom = preset === 'custom';
        const spanDays = (() => {
            const start = new Date(range.startDate);
            const end = new Date(range.endDate);
            if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 30;
            return Math.max(1, Math.round((end - start) / 86400000) + 1);
        })();

        // The smallest window that still contains the custom span, so the chart
        // never shows less than what was asked for.
        const analyticsPeriod = isCustom
            ? (PRESETS.find((option) => option.days >= spanDays) || PRESETS[PRESETS.length - 1]).value
            : preset;

        const label = isCustom
            ? `${range.startDate} to ${range.endDate}`
            : PRESETS.find((option) => option.value === preset)?.label || 'Last 30 days';

        return {
            preset,
            isCustom,
            startDate: range.startDate,
            endDate: range.endDate,
            spanDays,
            analyticsPeriod,
            analyticsPeriodLabel: PRESETS.find((option) => option.value === analyticsPeriod)?.label || '',
            label,
            setPreset: (next) => {
                setPresetValue(next);
                if (next !== 'custom') setRange(rangeForPreset(next));
            },
            setCustomRange: (startDate, endDate) => {
                if (!startDate || !endDate) return;
                // Swapped dates come back empty from every endpoint rather than
                // erroring, so order them here instead.
                const ordered = startDate <= endDate ? [startDate, endDate] : [endDate, startDate];
                setPresetValue('custom');
                setRange({ startDate: ordered[0], endDate: ordered[1] });
            }
        };
    }, [preset, range]);

    return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
};

/** Returns null outside the provider so a screen can be rendered on its own
 *  (a test, or a route mounted before the shell) without crashing. */
export const useDateRange = () => useContext(DateRangeContext);
